// hooks/useDriverJobs.js
// React binding for the Hauler job feed. Single read path.
//
// Returns { available, claimed, completed, loading, error, refetch }.
// Step 3 consumes `available` for JobsScreen. Step 5/7/9 consume the
// other slices.
//
// Eligibility filtering mirrors DriverScreen.loadJobs(): base-access
// gate, market match. Jobs missing market_id pass through (default
// market resolution happens in useJobEconomics).
//
// Bucket A+B scope: no viability filter, no surge fields. Phase 1.6
// per docs/refactor-plan.md.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../services/supabaseService";
import { isPlatformPaused } from "../services/communityAlertsService";

// Aligned with DriverScreen.loadJobs (line 289) so /haulers active section
// shows the same status pool as /drivers driver-path. 'scheduled' is the
// legacy admin-assigned bucket retained for backward compat per CLAUDE.md.
const ACTIVE_MINE_STATUSES = ["claimed", "scheduled", "en_route", "in_progress"];
const COMPLETED_STATUSES = ["complete"];

function normalizeRequestJob(row) {
  if (!row) return row;
  return {
    ...row,
    addr: row.addr || row.full_address || row.address || "",
    lat: row.lat ?? row.latitude ?? row.pickup_lat ?? row.pickup_latitude ?? null,
    lng: row.lng ?? row.longitude ?? row.pickup_lng ?? row.pickup_longitude ?? null,
    zone: row.zone || null,
    size: row.size || row.driver_verified_tier || row.prediction_variance_tier || null,
    quick: Boolean(row.quick || row.is_priority),
    placement: row.staging_location || row.placement_location || row.confirmed_staging_location || null,
  };
}

export function useDriverJobs({ session, driverProfile } = {}) {
  const [available, setAvailable] = useState([]);
  const [claimed, setClaimed] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchSeqRef = useRef(0);

  const userId = session?.user?.id || null;

  const refetch = useCallback(async () => {
    const fetchSeq = ++fetchSeqRef.current;
    if (!userId) {
      setAvailable([]);
      setClaimed([]);
      setCompleted([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Two independent gates skip the available-pool fetch:
      //   1. driverProfile.is_available === false  (Hauler turned themselves off)
      //   2. isPlatformPaused() === true           (admin paused via Tier 3 alert)
      // Active and completed slices continue to fetch in both cases —
      // forward-only continuation invariant (Phase 3 Q1=C). Already-
      // claimed work is unaffected by either gate.
      //
      // is_available default: `undefined` while profile loads is treated
      // as "available" (matches the column's NOT NULL DEFAULT true).
      // Platform-paused default: a transient lookup failure resolves
      // to false (treat as not-paused) so a flaky query doesn't hide
      // jobs from Haulers; the worst case is a single refetch where a
      // pause briefly looks lifted, corrected on the next tick.
      const profileLoading = driverProfile === undefined;
      const driverIsAvailable = driverProfile?.is_available !== false;
      let platformPaused = false;
      try { platformPaused = await isPlatformPaused(); } catch (_) { /* default false */ }
      const skipAvailable = profileLoading || !driverIsAvailable || platformPaused;
      const availPromise = skipAvailable
        ? Promise.resolve({ data: [], error: null })
        : supabase.from("requests").select("*")
            .is("assigned_driver", null)
            .eq("status", "available")
            .order("created_at", { ascending: true });

      const [availRes, mineRes, doneRes] = await Promise.all([
        availPromise,
        supabase.from("requests").select("*")
          .eq("assigned_driver", userId)
          .in("status", ACTIVE_MINE_STATUSES)
          .order("created_at", { ascending: true }),
        supabase.from("requests").select("*")
          .eq("assigned_driver", userId)
          .in("status", COMPLETED_STATUSES)
          .order("completed_at", { ascending: false })
          .limit(50),
      ]);
      if (availRes.error) throw availRes.error;
      if (mineRes.error) throw mineRes.error;
      if (doneRes.error) throw doneRes.error;
      if (fetchSeq !== fetchSeqRef.current) return;

      // Strict default matches DriverScreen.loadJobs (line 300): if profile
      // is missing has_base_access, the user is treated as not-yet-approved
      // and sees no jobs. Avoids the 'permissive default while profile loads'
      // flash that briefly leaks all available rows.
      const driverMarket = driverProfile?.market_id || null;
      const driverHasBaseAccess = driverProfile?.has_base_access || false;

      const eligible = (availRes.data || []).filter(j => {
        if (!driverHasBaseAccess) return false;
        if (driverMarket && j.market_id && j.market_id !== driverMarket) return false;
        return true;
      });

      if (profileLoading) {
        // Do not publish a false "0 available" state from the profile-loading
        // pass. The profile-backed pass will run next and own the pool.
        setAvailable([]);
      } else {
        setAvailable(eligible.map(normalizeRequestJob));
      }
      setClaimed((mineRes.data || []).map(normalizeRequestJob));
      setCompleted((doneRes.data || []).map(normalizeRequestJob));
      if (profileLoading) return;
    } catch (e) {
      if (fetchSeq !== fetchSeqRef.current) return;
      setError(e);
    } finally {
      if (fetchSeq === fetchSeqRef.current) {
        setLoading(driverProfile === undefined);
      }
    }
  }, [userId, driverProfile?.market_id, driverProfile?.has_base_access, driverProfile?.is_available]);

  useEffect(() => { refetch(); }, [refetch]);

  return { available, claimed, completed, loading, error, refetch };
}
